import { Button, Input, Link } from '@village/ui';
import { PatientInformationCard } from 'components/patient-information-card';
import { SelectedAppointmentDetailsCard } from 'components/selected-appointment-details-card';
import {
    useBookAppointment,
    useBooking,
    useCaptureCountlyEvent,
    useProviders,
    useScrollToTop,
    useSyncBookingWithUrl,
} from 'hooks';
import { useSubmitGoogleConversionToken } from 'hooks/use-submit-google-conversion-token';
import { useCallback, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { handleBookAppointmentError } from 'utils/appointment';

import * as Styled from './styles';

let lastBookAppointmentApiErrorStatus: number | undefined;

const ReviewDetailsPage = () => {
    useScrollToTop();

    const navigate = useNavigate();
    useSyncBookingWithUrl();
    const [searchParams] = useSearchParams();
    const { booking, setBookingField } = useBooking();
    const { data: providers } = useProviders(booking);
    const { addCountlyEvent } = useCaptureCountlyEvent();
    const [agreedOnTerms, setAgreedOnTerms] = useState(false);
    const { mutateAsync, isPending } = useBookAppointment(booking);
    const { submitGoogleConversionToken } = useSubmitGoogleConversionToken();
    const { patientInformation, reasonDetails, appointment } = booking;
    const reasonDetailsLength = reasonDetails?.length ?? 0;
    const helperText = `${reasonDetailsLength}/200 characters`;
    const errorText = reasonDetailsLength > 200 ? `Character limit exceeded: ${reasonDetailsLength}/200` : null;

    const appointmentSlotOrigin =
        // attempt to grab value from widget redirect URL query params
        searchParams.get('appointmentSlotOrigin') ??
        (window.location.pathname.includes('book-an-appointment') ? 'scheduler-main' : 'scheduler-embedded');

    const onConfirm = useCallback(async () => {
        if (appointment) {
            try {
                const { data } = await mutateAsync({});
                const provider = providers.find(({ providerid }) => providerid === appointment.providerid);

                addCountlyEvent({
                    key: 'appointmentBooked',
                    segmentation: {
                        appointmentSlotOrigin,
                        appointmentId: appointment.appointmentid,
                        appointmentDate: appointment.date,
                        appointmentTypeId: appointment.appointmenttypeid,
                        isPrn: !!provider?.isprn,
                        providerId: appointment.providerid,
                        providerName: provider?.displayname,
                        time: appointment.starttime,
                        isThawedAppointment: data[0]?.isthawed,
                        lastBookAppointmentApiErrorStatus,
                    },
                });

                await submitGoogleConversionToken();
                navigate('/confirmation', { replace: true });
            } catch (error) {
                const apiErrorStatus = handleBookAppointmentError(error, setBookingField, addCountlyEvent);

                /**
                 * This should help us identify the error that
                 * the user was able to recover from and eventually book an appointment
                 */
                lastBookAppointmentApiErrorStatus = apiErrorStatus;
            }
        }
    }, [
        appointment,
        mutateAsync,
        providers,
        addCountlyEvent,
        appointmentSlotOrigin,
        submitGoogleConversionToken,
        navigate,
        setBookingField,
    ]);

    if (!patientInformation) {
        return (
            <Navigate
                to={{ pathname: '/patient-information', search: searchParams.toString() }}
                replace={true}
                state={{ patientInformationMissing: true }}
            />
        );
    }

    return (
        <Styled.Container>
            <Styled.AppointmentDetailsCardContainer>
                <Styled.PageTitle type="h3">Review details</Styled.PageTitle>

                <PatientInformationCard />

                <Styled.FormContainer>
                    <Input
                        type="textarea"
                        label="Tell us more about the reason for your visit (optional)"
                        aria-label="Reason for visit"
                        rows={5}
                        fullWidth={true}
                        helperText={helperText}
                        error={errorText}
                        onChange={(event) => setBookingField({ reasonDetails: event.currentTarget.value })}
                        value={reasonDetails}
                    />

                    <Styled.Checkbox
                        data-testid="terms-agreement-check"
                        id="terms-agreement-check"
                        onChange={(_event, checked) => setAgreedOnTerms(checked)}
                        label={
                            <span>
                                I agree to{' '}
                                <Link href="https://www.villagemedical.com/terms-and-conditions" target="_blank">
                                    Terms of Use
                                </Link>{' '}
                                and{' '}
                                <Link href="https://www.villagemedical.com/privacy-policy" target="_blank">
                                    Privacy Policy
                                </Link>
                            </span>
                        }
                    />
                </Styled.FormContainer>
                <Styled.ConfirmationButtonContainer>
                    <Button
                        onClick={onConfirm}
                        disabled={!agreedOnTerms || isPending || !appointment || !patientInformation || !!errorText}
                        data-testid="button-book-appointment"
                        fullWidth={true}
                    >
                        {isPending ? 'Booking...' : 'Book Appointment'}
                    </Button>
                </Styled.ConfirmationButtonContainer>
            </Styled.AppointmentDetailsCardContainer>

            <SelectedAppointmentDetailsCard />
        </Styled.Container>
    );
};

export { ReviewDetailsPage };
